// Detects PWA install state + the platform-specific install path, and
// captures Chrome's `beforeinstallprompt` event so the rest of the app
// can fire the native install dialog on demand.
//
// Browser-imposed limits worth knowing:
//   - iOS Safari has no programmatic install API. Best we can do is show
//     a tutorial.
//   - On iOS, only Safari can install PWAs — Chrome/Firefox/Edge on iPhone
//     can't, because they're all WebKit shells.
//   - Android Chrome fires `beforeinstallprompt` once when the PWA passes
//     its install criteria (manifest + HTTPS + service worker is optional
//     for Chrome's promotion banner). We capture the event and call
//     `prompt()` later to trigger the native install dialog.
import { useState, useEffect } from 'react';

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // PWA-mode detection. iOS uses navigator.standalone (legacy); Android +
    // desktop expose it via the display-mode media query.
    const standalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onBeforeInstall = (e) => {
      // Stop Chrome's default mini-infobar so we can show the prompt at
      // a moment of our choosing instead.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setIsStandalone(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(typeof window !== 'undefined' && window.MSStream);
  const isAndroid = /Android/.test(ua);
  // iOS Safari excludes Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS),
  // Opera (OPiOS) — all of which are forced to be WebKit on iOS but
  // can't install PWAs via the share sheet.
  const isIOSSafari = isIOS && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  const isAndroidChrome = isAndroid && /Chrome/.test(ua) && !/SamsungBrowser/.test(ua);
  const isMobile = isIOS || isAndroid;

  const platform =
    isIOSSafari        ? 'ios-safari'      :
    isIOS              ? 'ios-other'       :  // iOS Chrome/Firefox/Edge — must use Safari
    isAndroidChrome    ? 'android-chrome'  :  // can call native prompt
    isAndroid          ? 'android-other'   :  // android Firefox / Samsung etc.
                         'desktop';

  async function trigger() {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null); // single-use; Chrome won't re-fire it
      return { outcome: choice.outcome }; // 'accepted' | 'dismissed'
    } catch (_) {
      return { outcome: 'error' };
    }
  }

  return {
    isStandalone,
    isMobile,
    isIOS,
    isAndroid,
    isIOSSafari,
    platform,
    canTriggerNativePrompt: !!deferredPrompt,
    trigger,
  };
}

// localStorage helper for persisting "user dismissed the auto-prompt".
// Centralized here so Layout (auto-show) and ProfileDropdown (manual
// re-open) can both consult and update the same key.
export const INSTALL_DISMISSED_KEY = 'silverservice:installPromptDismissed';

export function isInstallPromptDismissed() {
  if (typeof window === 'undefined') return false;
  try { return !!localStorage.getItem(INSTALL_DISMISSED_KEY); }
  catch (_) { return false; }
}

export function markInstallPromptDismissed() {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now())); }
  catch (_) {}
}
